import type { Seat } from '@cineamo/ui/src/seat-map/typedefs';

// Port of the mobile app's best-seat-algorithm.ts. Plan units (mm); the front row has the
// largest y. Only type imports, so node --test can load it without an install.

const NON_SELECTABLE: ReadonlySet<string> = new Set(['wheelchair', 'disabled']);
const FRONT_HALF_PENALTY = 100000;

const isFree = (seat: Seat) => !seat.isLocked && !NON_SELECTABLE.has(seat.type);

/**
 * Seats side by side with no seat of the row (taken or not) between them. The distance cap only tells an aisle
 * from a neighbor: it is 3 widths, as rooms with wider seats space them at 2 widths (centers 2000 mm apart on
 * 1000 mm seats), which the old `< 2 widths` test rejected. A taken seat between is caught by the in-between check.
 */
function contiguousGroups(free: Seat[], count: number, all: Seat[]): Seat[][] {
    const rows = new Map<string, Seat[]>();
    for (const s of free) {
        const row = rows.get(s.rowName) ?? [];
        row.push(s);
        rows.set(s.rowName, row);
    }
    const groups: Seat[][] = [];
    for (const row of rows.values()) {
        row.sort((a, b) => a.position.x - b.position.x);
        for (let i = 0; i + count <= row.length; i++) {
            const group = row.slice(i, i + count);
            const isContiguous = group.every((s, idx) => {
                if (idx === 0) return true;
                const prev = group[idx - 1];
                const [from, to] = [prev.position.x, s.position.x].sort((a, b) => a - b);
                const hasSeatBetween = all.some(
                    (o) =>
                        o.rowName === s.rowName &&
                        o.id !== s.id &&
                        o.id !== prev.id &&
                        o.position.x > from &&
                        o.position.x < to
                );
                return (
                    !hasSeatBetween &&
                    to - from < prev.width * 3 &&
                    Math.abs(s.position.y - prev.position.y) < prev.width * 0.5
                );
            });
            if (isContiguous) groups.push(group);
        }
    }
    return groups;
}

/**
 * The cinema's own pick, when it has made one (CIN-15322).
 *
 * A house knows things the geometry cannot: that its Loge is the best row, that the back block has the
 * only good sightline. Where seats are flagged, a free contiguous group among them wins outright — the
 * point of the setting is to override the guess, not to nudge it.
 *
 * Falls through to the heuristic when the cinema flagged nothing, or when what it flagged cannot seat
 * this party any more. A pinned seat that is taken is not a reason to offer nothing.
 */
function pinnedBestSeats(seats: Seat[], count: number): Seat[] {
    const pinned = seats.filter((seat) => seat.isBestSeat === true);
    if (pinned.length === 0) return [];

    const groups = contiguousGroups(pinned.filter(isFree), count, seats);
    if (groups.length === 0) return [];

    // Several flagged blocks can fit: keep the choice stable rather than depending on seat order, and
    // read left to right within the earliest row the cinema flagged.
    return groups.sort((a, b) => a[0].position.y - b[0].position.y || a[0].position.x - b[0].position.x)[0];
}

export function recommendBestSeats(seats: Seat[], count = 2): Seat[] {
    if (seats.length === 0 || count < 1) return [];

    const pinned = pinnedBestSeats(seats, count);
    if (pinned.length > 0) return pinned;

    const xs = seats.map((s) => s.position.x);
    const ys = seats.map((s) => s.position.y);
    const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
    const centerY = (Math.min(...ys) + Math.max(...ys)) / 2;

    let best: { group: Seat[]; score: number } | null = null;
    for (const group of contiguousGroups(seats.filter(isFree), count, seats)) {
        const posX = group.reduce((sum, s) => sum + s.position.x, 0) / group.length;
        const posY = group.reduce((sum, s) => sum + s.position.y, 0) / group.length;
        const distX = Math.abs(posX - centerX);
        const distY = Math.abs(posY - centerY);
        const frontPenalty = posY > centerY ? FRONT_HALF_PENALTY : 0;
        const distToBack = Math.max(0, centerY - posY);
        const horizontalPenalty = Math.max(0, distX - distToBack);
        const score = -distY - distX - frontPenalty - horizontalPenalty * 2;
        if (!best || score > best.score) best = { group, score };
    }
    return best?.group ?? [];
}
